import {getEventTicketLimitPerOrder} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {Counter} from '@wix/wix-ui-tpa/cssVars'
import {useTranslation} from '@wix/yoshi-flow-editor'
import React, {useCallback, useEffect} from 'react'
import {SEATING_ERROR} from '../../../../../constants'
import {useAppActions, useAppSelector} from '../../../../../hooks/runtime'
import {getEvent} from '../../../../../selectors/event'
import {
  getSelectedPlaceId,
  getShowPricingOptionsPlace,
  isInAccessibilityMode,
  isInDescription,
} from '../../../../../selectors/seating/mode'
import {getPlaceStock, isPlaceArea, isPlaceInBasket} from '../../../../../selectors/seating/place'
import {getOrderRemainder, getPlaces} from '../../../../../selectors/seating/places'
import {getSelectedTicketsQuantity} from '../../../../../selectors/selected-tickets'
import {TicketAreaCounterProps} from './interfaces'
import {classes} from './ticket-area-counter.st.css'

export const TicketAreaCounter = ({
  place,
  value,
  setValue,
  selectedPricingOptionId,
  origin,
}: TicketAreaCounterProps) => {
  const {t} = useTranslation()

  const {quantity, ticket, donation} = place
  const inBasket = isPlaceInBasket(place)
  const area = isPlaceArea(place)
  const {setAreaCounterValue, setPlaceQuantity, setShowPlaceDescription, setPlaceDonationError, setSeatingError} =
    useAppActions()

  const event = useAppSelector(getEvent)
  const ticketQuantity = useAppSelector(getSelectedTicketsQuantity)
  const inDescription = useAppSelector(isInDescription)
  const places = useAppSelector(getPlaces)
  const orderRemainder = useAppSelector(state => getOrderRemainder(state, place))
  const selectedPlaceId = useAppSelector(getSelectedPlaceId)
  const showAccessibilityMode = useAppSelector(isInAccessibilityMode)
  const pricingOptionsPlace = useAppSelector(getShowPricingOptionsPlace)

  const ticketsLeft = getEventTicketLimitPerOrder(event) - ticketQuantity
  const placeStock = getPlaceStock(places, place)
  const placeId = selectedPricingOptionId ?? place.id

  useEffect(() => {
    if (showAccessibilityMode && quantity === 0) {
      setAreaCounterValue({placeId, count: 1})
    }
  }, [showAccessibilityMode])

  const min = 1
  const max = Math.max(1, placeStock + quantity)

  const handleCounterChange = useCallback(
    (rawValue: string) => {
      let newValue = Number(rawValue)

      if (rawValue === '' || !Number.isInteger(newValue)) {
        return
      }

      if (newValue < min) {
        newValue = min
      } else if (newValue > max) {
        newValue = max
      }

      const ticketsExhausted = !!pricingOptionsPlace && newValue > ticketsLeft

      if (newValue > orderRemainder || ticketsExhausted) {
        setSeatingError({error: SEATING_ERROR.TICKET_LIMIT_REACHED})
        return
      }

      const shouldAddToBasket = !(inBasket && area && !!pricingOptionsPlace)

      if (inBasket && shouldAddToBasket) {
        setPlaceQuantity({
          placeId,
          count: newValue,
          origin,
          validateDonation: false,
        })

        if (inDescription) {
          setShowPlaceDescription({...place, quantity: newValue})
        }
      }

      setValue?.(newValue)
    },
    [
      max,
      min,
      value,
      orderRemainder,
      setSeatingError,
      setPlaceDonationError,
      showAccessibilityMode,
      placeId,
      ticket,
      donation,
    ],
  )

  if (value === undefined) {
    setAreaCounterValue({placeId, count: 1})
  }

  return (
    <div onClick={e => e.stopPropagation()}>
      <Counter
        data-hook={DH.TICKET_AREA_COUNTER}
        className={classes.counter}
        inputAriaLabel={t('seatings_area_counter_input')}
        errorTooltipMaxWidth={300}
        value={value}
        min={min}
        max={pricingOptionsPlace ? placeStock : max}
        onChange={handleCounterChange}
        aria-pressed={selectedPlaceId === place.id}
      />
    </div>
  )
}
